import { AxiosResponse } from 'axios';
import axiosInstance from 'services/axios/axios';
import { then } from 'services/axios/_utility';

/**
 * Fetch Departments
 * @param {string} queryParam user input
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<AxiosResponse<object[]>>} array of departments
 */
export const fetchStateList = async dispatch => {
	return await axiosInstance
		.get('api/Applications/EpicLink/States')
		.then(response => then(response, payload => payload, dispatch))
		.catch(ex => {
			throw ex;
		});
};

export const fetchJobCategoryData = async (queryParam, dispatch) => {
	return await axiosInstance
		.get('api/Applications/EpicLink/JobCategories', {
			params: {
				value: queryParam,
			},
		})
		.then(response => then(response, payload => payload, dispatch))
		.catch(ex => {
			throw ex;
		});
};

export const fetchProviderJobCategories = async dispatch => {
	return await axiosInstance
		.get('api/Applications/EpicLink/JobCategories/ProviderJobCategories')
		.then(response => then(response, payload => payload, dispatch))
		.catch(ex => {
			throw ex;
		});
};

export const fetchNpiDataByTN = async (value, trackingNumber) => {
	return await axiosInstance
		.get(
			`api/Applications/EpicLink/NPIValidation/${value}/${trackingNumber}`,
		)
		.then(response => response.data)
		.catch(ex => {
			throw ex;
		});
};

export const fetchNpiData = async (value, lastName, firstName) => {
	return await axiosInstance
		.get(
			`api/Applications/EpicLink/NPIValidation/${value}/${lastName}/${firstName}`,
		)
		.then(response => response.data)
		.catch(ex => {
			throw ex;
		});
};

// Flattens an NPI Registry result into the columns shown in the physician search results.
const normalizePhysician = physician => {
	const address = physician.addresses?.[0];

	return {
		npi: String(physician.number ?? ''),
		firstName: physician.basic?.first_name ?? '',
		lastName: physician.basic?.last_name ?? '',
		middleName: physician.basic?.middle_name ?? '',
		credential: physician.basic?.credential ?? '',
		address: address
			? [
				address.address_1,
				address.city,
				[address.state, address.postal_code]
					.filter(Boolean)
					.join(' '),
			]
				.filter(Boolean)
				.join(', ')
			: '',
		telephone: address?.telephone_number ?? '',
	};
};

/**
 * Search for supervising physicians.
 * @param {{firstName: string, lastName: string, npi: string, state: string}} criteria search criteria
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<object[]>} array of matching physicians
 */
export const fetchSupervisingPhysicians = async (
	{ firstName = '', lastName = '', npi = '', state = '' },
	dispatch,
) => {
	return await axiosInstance
		.get('api/Utilities/NPI/Search', {
			params: {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				npi: npi.trim(),
				state: state.trim(),
			},
		})
		.then(response =>
			then(
				response,
				payload => (payload ?? []).map(normalizePhysician),
				dispatch,
			),
		)
		.catch(ex => {
			throw ex;
		});
};

/**
 * Fetch Location options
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise<AxiosResponse<object[]>>} array of locations
 */
export const fetchLocationData = async dispatch => {
	return await axiosInstance
		.get('api/Applications/EpicLink/Locations')
		.then(response => then(response, payload => payload, dispatch))
		.catch(ex => {
			throw ex;
		});
};
